import { useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../ui/Button/Button';
import S from './GroupsSettingStyles';
import { Group } from '../../API/types/Groups';
import useGetGroups from '../../API/hooks/groups/useGetGroups';
import Modal from '../common/Modal/Modal';
import ActionOnGroupModal from './ActionOnGroupModal/ActionOnGroupModal';
import useAddUserToGroup from '../../API/hooks/groups/useAddUserToGroup';
import useAddVehicleToGroup from '../../API/hooks/groups/useAddVehicleToGroup';
import useDeleteVehicleFromGroup from '../../API/hooks/groups/useDeleteVehicleFromGroup';
import useDeleteUserFromGroup from '../../API/hooks/groups/useDeleteUserFromGroup';
import useDeleteGroup from '../../API/hooks/groups/useDeleteGroup';

type Props = {
  entity: 'vehicle' | 'user';
  entityId: string;
  fleetId: string;
  onClose: () => void;
  groups?: Group[];
};

const compareByName = (a: any, b: any): number => {
  if (a.name < b.name) {
    return -1;
  }
  if (a.name > b.name) {
    return 1;
  }
  return 0;
};

function GroupsSetting({
  onClose, entity, entityId, groups, fleetId,
}: Props): JSX.Element {
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const [addModalOpen, toggleAddModalOpen] = useState(false);
  const [deleteModalOpen, toggleDeleteModalOpen] = useState(false);
  const [actionGroup, setActionGroup] = useState<string | undefined>(undefined);

  const onAddSubmitSuccess = (): void => {
    toggleAddModalOpen(false);
    setActionGroup(undefined);
    setSubmitError(null);
    if (entity === 'user') {
      toast.success('Group access updated. The user must log out and log back in for this change to take effect.', { duration: 6000 });
    }
  };
  const onAddSubmitFail = (errorMessage: string): void => {
    setSubmitError(errorMessage);
    toggleAddModalOpen(false);
    setActionGroup(undefined);
  };
  const onRemoveSubmitSuccess = (): void => {
    toggleDeleteModalOpen(false);
    setActionGroup(undefined);
    setSubmitError(null);
    if (entity === 'user') {
      toast.success('Group access updated. The user must log out and log back in for this change to take effect.', { duration: 6000 });
    }
  };
  const onRemoveSubmitFail = (errorMessage: string): void => {
    setSubmitError(errorMessage);
    toggleDeleteModalOpen(false);
    setActionGroup(undefined);
  };
  const onDeleteGroupSubmitSuccess = (): void => { };
  const onDeleteGroupSubmitFail = (errorMessage: string): void => {
    setSubmitError(errorMessage);
    setActionGroup(undefined);
    setSubmitError(null);
  };

  const { data: allGroups, isLoading: allGroupsLoading } = useGetGroups(fleetId, '?limit=1000&skip=0');
  const currentGroups = groups || [];
  const currentGroupsIds = currentGroups.map((g: Group) => g._id);
  const availableGroups = allGroups?.results.filter((g: Group) => !currentGroupsIds.includes(g._id)) || [];

  const { mutate: addVehicleToGroup } = useAddVehicleToGroup(onAddSubmitSuccess, onAddSubmitFail);
  const { mutate: addUserToGroup } = useAddUserToGroup(onAddSubmitSuccess, onAddSubmitFail);
  const { mutate: removeVehicleFromGroup } = useDeleteVehicleFromGroup(onRemoveSubmitSuccess, onRemoveSubmitFail);
  const { mutate: removeUserFromGroup } = useDeleteUserFromGroup(onRemoveSubmitSuccess, onRemoveSubmitFail);
  const { mutate: removeGroup } = useDeleteGroup(onDeleteGroupSubmitSuccess, onDeleteGroupSubmitFail);

  const handleAddToGroup = (entitiesCountInGroup?: number): void => {
    if (entity === 'vehicle') {
      addVehicleToGroup({
        fleetId,
        groupId: actionGroup || '',
        vehicleId: entityId,
      });
    } else {
      addUserToGroup({
        fleetId,
        groupId: actionGroup || '',
        userId: entityId,
      });
    }
  };

  const handleRemoveFromGroup = (entitiesCountInGroup?: number): void => {
    if (entity === 'vehicle') {
      const group = `${actionGroup}`;
      removeVehicleFromGroup({
        fleetId,
        groupId: group,
        vehicleId: entityId,
      });

      // This part of functionality is under BE
      // if (entitiesCountInGroup === 1) {
      //   removeGroup({
      //     fleetId,
      //     id: group,
      //   });
      // }
    } else {
      removeUserFromGroup({
        fleetId,
        groupId: actionGroup || '',
        userId: entityId,
      });
    }
  };

  return (
    <S.Wrapper>
      <div className="title">
        {entity === 'vehicle' ? 'Vehicle' : 'User'} is assigned to:
      </div>
      <div className="groups">
        {currentGroups.sort(compareByName).map((g: any) => (
          <p key={g._id}>
            <div className="group-name" title={g.name}>{g.name}</div>
            <button
              type="button"
              className="delete-btn"
              title="Remove from group"
              onClick={() => {
                setActionGroup(g._id);
                toggleDeleteModalOpen(true);
              }}
            >x
            </button>
          </p>
        ))}
        {!currentGroups.length && groups && (<div className="no-groups">no assigned groups</div>)}
        {!groups && (<div className="no-groups">Loading...</div>)}
      </div>
      <div className="title">
        Add {entity} to new group
      </div>
      <div className="groups">
        {availableGroups.sort(compareByName).map((g: any) => (
          <p key={g._id}>
            <div className="group-name" title={g.name}>{g.name}</div>
            <button
              type="button"
              className="add-btn"
              title={`Add ${entity} to group`}
              onClick={() => {
                setActionGroup(g._id);
                toggleAddModalOpen(true);
              }}
            >+
            </button>
          </p>
        ))}
        {!availableGroups.length && !allGroupsLoading && (<div className="no-groups">no available groups</div>)}
        {allGroupsLoading && (<div className="no-groups">Loading...</div>)}
      </div>
      {!!submitError && (
        typeof submitError === 'string'
          ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
          : submitError.map((error) => <S.ErrorMessage>{error}</S.ErrorMessage>)
      )}
      <div className="actions">
        <Button
          className="button"
          padding="7px 40px"
          onClick={onClose}
        >Close
        </Button>
      </div>
      {addModalOpen && (
        <Modal
          title="Confirm add to group"
          state={addModalOpen}
          onClose={() => setActionGroup(undefined)}
          toggleState={toggleAddModalOpen}
        >
          <ActionOnGroupModal
            title={`Are you sure you want to add ${entity} to this group?`}
            entity={entity}
            action="add"
            onClose={() => toggleAddModalOpen(false)}
            onAction={handleAddToGroup}
          />
        </Modal>
      )}
      {deleteModalOpen && (
        <Modal
          title="Confirm remove from group"
          state={deleteModalOpen}
          toggleState={toggleDeleteModalOpen}
        >
          <ActionOnGroupModal
            title={`Are you sure you want to remove ${entity} from this group?`}
            entity={entity}
            groupId={actionGroup}
            fleetId={fleetId}
            action="delete"
            onClose={() => toggleDeleteModalOpen(false)}
            onAction={handleRemoveFromGroup}
          />
        </Modal>
      )}
    </S.Wrapper>
  );
}

export default GroupsSetting;
